import React, { RefAttributes, RefObject, useEffect, useMemo, useRef } from "react";
import { Unsubscribe } from "redux";
import { useDispatch, useStore } from "react-redux";
import classNames from "classnames";
import * as Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Models, Types, Services, Logger, Utils, Colors } from "@sendum/core";

import { ILollipopData } from "../interfaces/ILollipopData";
import { IVisibilityState } from "../store/VisibilityReducer";
import { VisibilityRootState } from "../store/VisibilityStore";
import * as VGUtils from "./VisibilityGraphsTimelineUtils";
import { VisibilityGraphsLegend } from "./VisibilityGraphsLegend";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityGraphsTimeline");

import classes from "./VisibilityGraphsTimeline.module.scss";
import * as VisibilityActions from "../store/VisibilityActions";

Utils.setupHighcharts();

type TSensorsHistories = Map<string, Types.TProperty[]>;

interface IProps {
	maxSensors: number;
	className?: string;
	virtualShipmentName?: string;
}

export const VisibilityGraphsTimeline = ({ maxSensors, className, virtualShipmentName }: IProps): JSX.Element => {
	logger.debug("render");

	const dispatch = useDispatch();
	const store = useStore<VisibilityRootState>();
	const storeUnsubscribe = React.useRef<Unsubscribe>();
	const chartContainerRef = React.useRef<HTMLDivElement>(null);

	let tooltipPoints: Highcharts.Point[] = [];
	let currentDeviceId = "";
	let currentSensors: string[] = [];
	const currentHistoryCounts: Map<string, number> = new Map<string, number>();
	let isCurrentLoading = false;
	let currentSegments: Models.Segment[] = [];

	React.useEffect(() => () => {
		Services.EventEmitter.off("chart");
		Services.EventEmitter.off("chart_close");
		Services.EventEmitter.off("chart_resetZoom");

		if (storeUnsubscribe.current) {
			storeUnsubscribe.current();
			window.removeEventListener("resize", () => onResizeHandler());
		}
	}, []);

	const resetZoom = (chart: Highcharts.Chart): void => {
		let nMin: number | null = null;
		let nMax: number | null = null;
		if (!chart.xAxis) {
			return;
		}
		chart.xAxis.forEach(x => {
			const { min, max } = x.getExtremes();

			if (!nMin || min < nMin) {
				nMin = min;
			}

			if (!nMax || max > nMax) {
				nMax = max;
			}
		});

		if (nMin !== null && nMax !== null) {
			for (const x of chart.xAxis) {
				x.setExtremes(nMin, nMax);
			}

			chart.redraw();
		}
	};

	const showTooltip = (epoch: number, chart: Highcharts.Chart): void => {
		if (!chart) {
			return;
		}

		if (tooltipPoints.length) {
			tooltipPoints.forEach(point => {
				point.setState("normal");
			});
			tooltipPoints = [];
		}

		chart.series?.forEach(series => {
			if (series.data.length !== 0) {
				const pointsAround: (number | Highcharts.Point)[][] = series.data
					.filter(p => p.x >= epoch - Utils.time.MILLISECONDS_OF_5MIN && p.x <= epoch + Utils.time.MILLISECONDS_OF_5MIN)
					.map(p => [Math.abs(p["x"] - epoch), p]);
				if (Boolean(pointsAround) && pointsAround.length) {
					const point: Highcharts.Point = pointsAround.reduce((min, p) => (min.x > (p[0] as number) ? p[1] as Highcharts.Point : min), pointsAround[0][1] as Highcharts.Point);
					if (point) {
						tooltipPoints.push(point);
					}
				}
			}
		});

		if (tooltipPoints.length) {
			chart.tooltip.refresh(tooltipPoints);
		}

		const toolTipPlotLine: Highcharts.XAxisPlotLinesOptions = {
			id: `PL-${epoch}`,
			color: VGUtils.readCSSColor(Colors.STORM_GRAY),
			width: 3,
			value: epoch,
			dashStyle: "ShortDot",
			zIndex: 1,
		};

		const xAxis: Highcharts.XAxisOptions = {
			plotLines: [...getPairPlotLines(store.getState().Visibility.device), toolTipPlotLine],
			plotBands: Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart() ? [...VGUtils.getTimeLinePlotBands(virtualShipmentName ? store.getState().Visibility.shipments : [store.getState().Visibility.shipment], store.getState().Visibility.device)] : undefined,
		};

		chart.update({ xAxis: [xAxis] }, true);
	};

	const hideTooltip = (chart: Highcharts.Chart): void => {
		tooltipPoints = [];
		chart.tooltip.hide(0);
	};

	const hideTooltipMap = (_epoch: any, chart: Highcharts.Chart): void => {
		chart.series.forEach(series => {
			series.data.forEach(point => {
				if (Math.round(point.x / 1000) === Math.round(_epoch / 1000)) {
					point.setState("normal");
				}
			});
		},
		);
		chart.tooltip.hide(0);
	};

	const getPairPlotLines = (device?: Models.Device): Highcharts.XAxisPlotLinesOptions[] => {
		if (device && device.pairHistory.length !== 0) {
			return device.pairHistory
				.filter(h => h.action === "pair")
				.map(h => ({
					color: VGUtils.readCSSColor(Colors.STORM_GRAY),
					width: 2,
					value: h.epoch,
					dashStyle: "Solid",
					zIndex: 1,
					id: "pair",
					label: {
						text: h.device,
						align: "right",
						rotation: 270,
						x: -5,
						y: 20,
					},
				}));
		}

		return [];
	};

	const showMapPopup = (time: number): void => {
		Services.EventEmitter.emit("map", time);
	};

	const timer = useRef<NodeJS.Timeout>();

	const onResizeHandler = (chart?: Highcharts.Chart): void => {
		if (chart && chartContainerRef.current) {
			chart.setSize(10, chartContainerRef.current.clientHeight);
			if (timer.current) {
				clearTimeout(timer.current);
				timer.current = undefined;
			}
			timer.current = setTimeout(() => {
				if (chartContainerRef.current) {
					chart.setSize(chartContainerRef.current.clientWidth, chartContainerRef.current.clientHeight);
				}
			}, 500);

		}
	};

	const onHighchartsRefChanged = (ref: React.PropsWithoutRef<HighchartsReact.Props> & RefAttributes<{ chart: Highcharts.Chart; container: RefObject<HTMLDivElement> }> | null): void => {
		if (storeUnsubscribe.current) {
			storeUnsubscribe.current();
			Services.EventEmitter.off("chart");
			Services.EventEmitter.off("chart_close");
			Services.EventEmitter.off("chart_resetZoom");
		}

		const chart = ref?.chart as Highcharts.Chart;

		if (chart) {
			Services.EventEmitter.on("chart", epoch => showTooltip(+epoch, chart));
			Services.EventEmitter.on("chart_close", epoch => hideTooltipMap(epoch, chart));
			Services.EventEmitter.on("chart_resetZoom", () => resetZoom(chart));
			chart.update(VGUtils.getDefaultHighchartsConfig(showMapPopup, maxSensors), true);
			storeUnsubscribe.current = store.subscribe(() => updateGraphs(chart));
			updateGraphs(chart);
			window.addEventListener("resize", () => onResizeHandler(chart));
		}
	};

	const updateGraphs = (chart: Highcharts.Chart): void => {
		const state = store.getState().Visibility;

		if (chart === null || isDeviceChanged(state.currentDeviceId)) {
			return;
		}

		isLoadingHandler(state, chart);
		sensorsHandler(state, chart);
		changeHistoryHandler(state, chart);
	};

	const isDeviceChanged = (deviceId: string | undefined): boolean => {
		if (deviceId && !currentDeviceId) {
			currentDeviceId = deviceId;

			return false;
		}

		if (deviceId && currentDeviceId !== deviceId) {
			currentDeviceId = deviceId;

			return true;
		}

		return false;
	};

	const isLoadingHandler = ({ device, sensors, history, historyLoading }: IVisibilityState, chart: Highcharts.Chart): void => {
		if (!device) {
			return;
		}

		const isLoading = sensors.length !== 0 && sensors.some((sensor: string) => historyLoading.get(`${device.id}#${device.shipment.id}#${sensor}`));

		if (isLoading !== isCurrentLoading) {
			if (isCurrentLoading) {
				updateYAxis(sensors, chart);
				updateChart(device, sensors, history, chart);
				chart.hideLoading();
			} else {
				chart.showLoading("Loading...");
			}
			isCurrentLoading = isLoading;
		}
	};

	const sensorsHandler = ({ device, sensors, history, historyLoading }: IVisibilityState, chart: Highcharts.Chart): void => {
		if (!device) {
			return;
		}

		if (sensors.length !== currentSensors.length || currentSensors.some(sensor => !sensors.includes(sensor)) || store.getState().Visibility.selectedSensor || JSON.stringify(sensors) !== JSON.stringify(currentSensors)) {
			currentSensors = sensors;
			removeSeriesOfUnselectedSensors(sensors, chart);
			updateYAxis(sensors, chart);
			const load = loadSensorsHistory(device, sensors, history, historyLoading, chart);
			if (!load) {
				updateChart(device, sensors, history, chart);
			}
		}
	};

	const changeHistoryHandler = ({ device, history, shipment }: IVisibilityState, chart: Highcharts.Chart): void => {
		if (!device) {
			return;
		}

		let needUpdateSeries = false;

		[...history.keys()].forEach(key => {
			const [, , sensorName] = key.split("#");
			if (currentSensors.includes(sensorName)) {
				const sensorHistoryCount = history.get(key)?.length;
				if ((Boolean(sensorHistoryCount) && Boolean(currentHistoryCounts.get(sensorName)) && currentHistoryCounts.get(sensorName) !== sensorHistoryCount) ||
                    (Boolean(sensorHistoryCount) && !currentHistoryCounts.get(sensorName))) {
					needUpdateSeries = true;
				}
			}
		});

		if (!currentSegments.length && shipment?.segments.length) {
			needUpdateSeries = true;
			currentSegments = JSON.parse(JSON.stringify(shipment?.segments)) as Models.Segment[];
		}

		if (currentSegments.length && shipment?.segments.length && (JSON.stringify(currentSegments) !== JSON.stringify(shipment?.segments))) {
			needUpdateSeries = true;
			currentSegments = JSON.parse(JSON.stringify(shipment?.segments)) as Models.Segment[];
		}

		if (needUpdateSeries) {
			updateChart(device, currentSensors, history, chart, true);
		}
	};

	const loadSensorsHistory = (device: Models.Device, sensors: string[], history: TSensorsHistories, historyLoading: Map<string, boolean>, chart: Highcharts.Chart): boolean => {
		let load = false;

		sensors.forEach((sensor: string) => {
			if (sensor === "setTempRange") {
				return;
			}
			const deviceHistory = history.get(`${device.id}#${virtualShipmentName || device.shipment.id}#${sensor}`);
			const isSensorLoading = historyLoading.get(`${device.id}#${device.shipment.id}#${sensor}`);
			if (!deviceHistory && !isSensorLoading) {
				chart.showLoading("Loading...");
				load = true;
			}
		});

		return load;
	};

	const removeSeriesOfUnselectedSensors = (sensors: string[], chart: Highcharts.Chart): void => {
		chart.series.forEach(_s => {
			if (_s.options.id === "highcharts-navigator-series") {
				return;
			}

			if (_s.options.id && !sensors.includes(_s.options.id)) {
				if (_s.options.id === VGUtils.LINE_DOOR_OPEN_ID && sensors.includes(VGUtils.DOOR_OPEN_ID)) {
					return;
				}
				if (_s.options.id === `${store.getState().Visibility.selectedSensor || ""}fullView`) {
					return;
				}
				const series = chart.get(_s.options.id);
				if (series) {
					series.remove(false);
				}
			}

			if (!sensors.includes(VGUtils.DOOR_OPEN_ID)) {
				const series = chart.get(VGUtils.LINE_DOOR_OPEN_ID);
				if (series) {
					series.remove(false);
				}
			}
		});
	};

	const updateYAxis = (sensors: string[], chart: Highcharts.Chart): void => {
		const yAxis: Highcharts.AxisOptions[] = VGUtils.getYAxis(sensors, store.getState().Visibility.selectedSensor, maxSensors, store.getState().Visibility.device);

		const currentYAxis = chart.yAxis
			.map(y => y.options.id)
			.filter((id: string) => !["highcharts-navigator-yaxis", "navigator-y-axis"].includes(id));

		currentYAxis.forEach((id: string) => {
			const axis = yAxis.find(y => y.id === id);
			if (!axis) {
				chart.get(id)?.remove(false);
			}
		});

		yAxis.forEach(y => {
			if (!currentYAxis.includes(y.id)) {
				chart.addAxis(y, false, false);
			} else if (y.id) {
				const yA = chart.get(y.id) as Highcharts.Axis;
				if (yA) {
					yA.update(y as never, false);
					if (y.id === "Temperature") {
						if (y.plotLines) {
							y.plotLines.forEach(pL => yA.addPlotLine(pL));
						} else {
							yA.removePlotLine("temperaturePlotLines");
						}
						if (y.plotBands) {
							y.plotBands.forEach(pB => yA.addPlotBand(pB));
						} else {
							yA.removePlotBand("temperaturePlotBand");
						}
					}
				}
			}
		});

		chart.redraw();

		if (sensors.length === 1 && sensors[0] === "setTempRange") {
			chart.addSeries({ id: "hiddenSeries", data: [], showInLegend: false, visible: false, yAxis: "Temperature", type: "xrange" }, true);
		}
	};

	const updateSeries = (chart: Highcharts.Chart, series: Highcharts.SeriesOptionsType[]): void => {
		const currentSeriesIds = chart.series.map(_ => _.options.id);
		const currentyAxisIds = chart.yAxis.map(y => y.options.id);
		series.forEach(_ => {
			const seriesId = _.id;
			if (seriesId && currentyAxisIds.includes(seriesId)) {
				if (!currentSeriesIds.includes(seriesId)) {
					chart.addSeries(_, false);
				} else if ((_.type === "line" || _.type === "lollipop" || _.type === "flags") && _.data) {
					const s = chart.series.find(cs => cs.userOptions.id === seriesId);
					if (s) {
						s.setData(_.data, false, false);
					}
				}
			}
		});
		Services.EventEmitter.off("chart");
		Services.EventEmitter.on("chart", epoch => showTooltip(+epoch, chart));

		chart.redraw(true);
	};

	const prepareData = (device: Models.Device, sensors: string[], history: TSensorsHistories): { xMin: number; xMax: number; histories: Map<string, number[][] | ILollipopData[]> } => {
		const histories: Map<string, number[][] | ILollipopData[]> = new Map<string, number[][] | ILollipopData[]>();
		const pairDates = device.pairHistory.map(h => h.epoch);
		let xMin: number = Math.min(...pairDates);
		let xMax: number = Math.max(...pairDates);

		if (sensors.length) {
			sensors.forEach(sensorName => {
				const sensorData = VGUtils.getHistoryBySensorName(device, history, sensorName, virtualShipmentName);
				currentHistoryCounts.set(sensorName, sensorData.length);

				const min = Math.min(...sensorData.map(d => d[0]));
				if (xMin === null || min < xMin) {
					xMin = min;
				}
				const max = Math.max(...sensorData.map(d => d[0]));
				if (xMax === null || max > xMax) {
					xMax = max;
				}

				if (sensorName !== "setTempRange") {
					const trueValue = VGUtils.getSensorBooleanValue(device, sensorName, true);
					const falseValue = VGUtils.getSensorBooleanValue(device, sensorName, false);
					if (sensorName === VGUtils.DOOR_OPEN_ID) {
						const filterData = sensorData.filter((d, i, arr) => {
							if (!i) {
								return d[1] !== 0;
							}

							return arr[i - 1][1] !== d[1] && d[1];
						});
						const data: ILollipopData[] = filterData.map(a => ({
							x: a[0],
							low: 1,
							y: 1,
							name: a[1] ? trueValue : falseValue,
						}));
						histories.set(sensorName, data);
					} else if (VGUtils.getSensorDataType(device, sensorName) === "boolean") {
						const data: ILollipopData[] = sensorData.map(a => ({
							x: a[0],
							y: a[1],
							name: a[1] ? trueValue : falseValue,
						}));
						histories.set(sensorName, data);
					} else {
						histories.set(sensorName, sensorData);
					}
				}
			});
		}

		if (Math.abs(xMin) === Infinity) {
			xMin = device.releasedDate || new Date().getTime() - Utils.time.MILLISECONDS_OF_HOUR;
		}
		if (Math.abs(xMax) === Infinity) {
			xMax = new Date().getTime();
		}

		xMin = xMin - Utils.time.MILLISECONDS_OF_5MIN;
		xMax = xMax + Utils.time.MILLISECONDS_OF_5MIN;

		return { xMin, xMax, histories };
	};

	const updateAxis = (chart: Highcharts.Chart, device: Models.Device, xMin: number, xMax: number, seriesLength: number, noScale?: boolean): void => {
		chart.update({
			xAxis: [
				{
					plotLines: getPairPlotLines(device),
					plotBands: Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart() ? VGUtils.getTimeLinePlotBands(virtualShipmentName ? store.getState().Visibility.shipments : [store.getState().Visibility.shipment], store.getState().Visibility.device) : undefined,
					min: seriesLength === 0 ? undefined : xMin,
					max: seriesLength === 0 ? undefined : xMax,
				},
			],
			navigator: {
				xAxis: {
					min: seriesLength === 0 ? undefined : xMin,
					max: seriesLength === 0 ? undefined : xMax,
				},
			},
		}, false);

		(chart.get("mainXAxis") as Highcharts.Axis)?.setTitle({
			align: "middle",
		}, false);

		chart.redraw();

		if (!noScale) {
			for (const x of chart.xAxis) {
				if (seriesLength === 0) {
					x.setExtremes();
				} else {
					x.setExtremes(xMin, xMax);
				}
			}
		} else {
			const extremes = VGUtils.getChartExtremes(chart);
			chart.xAxis.forEach((x: Highcharts.Axis, index: number) => {
				if (extremes[index]) {
					x.setExtremes(extremes[index].min, extremes[index].max);
				}
			});
		}
		chart.redraw();
	};

	const updateChart = (device: Models.Device, sensors: string[], history: TSensorsHistories, chart: Highcharts.Chart, noScale?: boolean): void => {
		const { xMin, xMax, histories } = prepareData(device, sensors, history);
		const series = buildListSeries(device, histories);

		hideTooltip(chart);
		updateSeries(chart, series);
		updateAxis(chart, device, xMin, xMax, series.length, noScale);
	};

	const buildListSeries = (device: Models.Device, histories: Map<string, number[][] | ILollipopData[]>): Highcharts.SeriesOptionsType[] => {
		const series: Highcharts.SeriesOptionsType[] = [];


		[...histories.keys()].forEach(sensorName => {
			series.push(VGUtils.buildTypedSeries(device, store.getState().Visibility.selectedSensor, sensorName, histories.get(sensorName)));

			if (sensorName === VGUtils.DOOR_OPEN_ID) {
				series.push(VGUtils.buildLollipoSeries(device, sensorName));
			}
		});

		if (Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart()) {
			series.push(VGUtils.buildTimelineSeries(virtualShipmentName ? store.getState().Visibility.shipments : [store.getState().Visibility.shipment], store.getState().Visibility.device));
		}

		return series;
	};


	const initialConfig = useMemo(() => store.getState().Visibility.device?.getSelectedSensorsConfig(maxSensors) ?? [],[maxSensors, store]);

	useEffect(() => {
		dispatch(VisibilityActions.resetSelectedSensor(initialConfig));
	},[initialConfig, dispatch]);

	return (
		<div className={classNames(className, classes.graphs)} data-testid="shipment-sensors-graph">
			<div className={classes.graphs__row}>
				<VisibilityGraphsLegend maxSensors={maxSensors} />
				<div ref={chartContainerRef} className={classes.graphs__chart}>
					<HighchartsReact
						ref={onHighchartsRefChanged}
						highcharts={Highcharts}
						options={VGUtils.getDefaultHighchartsConfig(showMapPopup, maxSensors)}
						constructorType="stockChart"
					/>
				</div>
			</div>
		</div>
	);
};
